import React from 'react';

import { DropdownOwnerships } from '../../../../../../shared/components/ui/atoms';
import { ONBOARDING_STEPS } from '../../../../../../utils/constants';

import { SpanOption } from '../../SpanOption';
import { ResumeContentContext } from '../../types';
import { getText } from '../../utils/getText';
import { SomeoneElseCreditInfo } from '../components/SomeoneElseCreditInfo';

/**
 * Calcula los elementos de fallback para FICO y propiedades
 */
export const calculateFallbackElements = (context: ResumeContentContext) => {
  const { ownershipSelected, steps, values } = context;
  
  const lastLoanData = !ownershipSelected?.isNew && ownershipSelected?.loans?.length
    ? ownershipSelected.loans[ownershipSelected.loans.length - 1]?.loanData
    : undefined;

  const fallbackFicoValue = lastLoanData?.["What's your credit score?"];
  const fallbackPropertiesValue = lastLoanData?.["How many properties have you purchased/sold in the last 24 months?"];

  const fallbackFicoElement = fallbackFicoValue
    ? <SpanOption text={getText(values, fallbackFicoValue, 5) || fallbackFicoValue} />
    : null;

  const fallbackPropertiesElement = fallbackPropertiesValue
    ? <SpanOption text={getText(values, fallbackPropertiesValue, 6) || fallbackPropertiesValue} />
    : null;

  const ficoElement = steps.ficoRange.isSet ? steps.ficoRange.element : fallbackFicoElement;
  const propertiesElement = steps.propertiesNegotiated.isSet ? steps.propertiesNegotiated.element : fallbackPropertiesElement;

  const ficoElementResolved = ficoElement || <SpanOption text='N/A' disableUnderline />;
  const propertiesElementResolved = propertiesElement || <SpanOption text='0 properties' disableUnderline />;

  return { ficoElementResolved, propertiesElementResolved };
};

/**
 * Calcula el índice del paso de detalles del guarantor
 */
export const calculateGuarantorDetailsStepIndex = (
  values: any,
  setSelectedIndex?: (index: number, options?: { force?: boolean }) => void
) => {
  const guarantorFirstName = values?.guarantorFirstName || '';
  if (!guarantorFirstName && setSelectedIndex) {
    const propertyKindStepIndex = ONBOARDING_STEPS.findIndex(
      (step) => step.text === 'What kind of property is it?'
    );

    if (propertyKindStepIndex !== -1) {
      const addressBaseIndex = 1;
      let flowIndex = addressBaseIndex + 1 + propertyKindStepIndex;

      const hasInvestmentAddress = typeof values?.['Investment property address'] === 'string' &&
        values['Investment property address'].trim().length > 0;
      const locationStepIndex = ONBOARDING_STEPS.findIndex(
        (step) => step.text === 'Where is the property located?'
      );

      if (hasInvestmentAddress && locationStepIndex !== -1 && propertyKindStepIndex > locationStepIndex) {
        flowIndex -= 1;
      }

      const guarantorDetailsStepIndex = flowIndex + 1;
      setSelectedIndex(guarantorDetailsStepIndex, { force: true });
    } else {
      const fallbackIndex = 1 + 1 + 4 + 1;
      setSelectedIndex(fallbackIndex, { force: true });
    }
  }
};

/**
 * Renderiza el bloque de información de crédito del borrower
 */
export const renderCreditInfoBlock = (
  isSomeoneElse: boolean,
  guarantor: ResumeContentContext['guarantor'],
  ownershipItems: any[],
  isCreditInfoLocked: boolean,
  canUnlockCreditInfo: boolean,
  ficoElementResolved: React.ReactNode,
  propertiesElementResolved: React.ReactNode,
  unlockCreditInfo: () => void,
  context: ResumeContentContext
) => {
  if (isSomeoneElse) {
    return (
      <>
        <strong>{guarantor.text || 'The guarantor'}</strong> has a FICO score of {ficoElementResolved} and has purchased/sold {propertiesElementResolved} in the past 36 months
      </>
    );
  }

  return (
    <>
      <DropdownOwnerships
        items={ownershipItems}
        disabled={isCreditInfoLocked}
      />{' '}
      has a FICO score of {ficoElementResolved} and has purchased/sold {propertiesElementResolved} in the past 36 months
      {isCreditInfoLocked && canUnlockCreditInfo && (
        <div className='change-credit-info-wrapper'>
          <SpanOption
            text='Change this information'
            onClick={unlockCreditInfo}
            className='change-credit-info'
          />
        </div>
      )}
    </>
  );
};

/**
 * Renderiza el bloque de información de crédito con callback onAddNew
 */
export const renderCreditInfoBlockWithAddNew = (
  isSomeoneElse: boolean,
  guarantor: ResumeContentContext['guarantor'],
  ownershipItems: any[],
  isCreditInfoLocked: boolean,
  canUnlockCreditInfo: boolean,
  ficoElementResolved: React.ReactNode,
  propertiesElementResolved: React.ReactNode,
  unlockCreditInfo: () => void,
  context: ResumeContentContext
) => {
  if (isSomeoneElse) {
    return (
      <>
        <SomeoneElseCreditInfo
          ownershipItems={ownershipItems}
          isCreditInfoLocked={isCreditInfoLocked}
          ficoElementResolved={ficoElementResolved}
          propertiesElementResolved={propertiesElementResolved}
          unlockCreditInfo={unlockCreditInfo}
          ownershipSelected={context.ownershipSelected}
          loanOwner={context.steps.loanOwner}
          values={context.values}
        />
      </>
    );
  }

  return (
    <>
      <DropdownOwnerships
        items={ownershipItems}
        disabled={isCreditInfoLocked}
        onAddNew={() => {
          calculateGuarantorDetailsStepIndex(context.values, context.setSelectedIndex);
        }}
      />{' '}
      has a FICO score of {ficoElementResolved} and has purchased/sold {propertiesElementResolved} in the past 36 months
      {isCreditInfoLocked && canUnlockCreditInfo && (
        <div className='change-credit-info-wrapper'>
          <SpanOption
            text='Change this information'
            onClick={unlockCreditInfo}
            className='change-credit-info'
          />
        </div>
      )}
    </>
  );
};

